import { useEffect, useRef, useState } from 'react';

import { FixedSizeList } from 'react-window';
import { Scrollbars } from '@opensumi/react-custom-scrollbars-2';

import { get } from '@/Utils/API';
import Icon from '@/Components/UI/Icon/Icon';
import Spinner from '@/Components/UI/Spinner/Spinner';
import { getFileUuidFromPath } from '@/Utils/getFileUuidFromPath';
import CheckboxInput from '@/Components/UI/Form/CheckboxInput/CheckboxInput';
import {
  AUTOCOMPLETE,
  QUERY_PARAM_OMIT_EMPTY_VALUES,
  FILTER_SELECT_ALL_LIMIT,
} from '@/Utils/constants';

import { Option } from './Option';
import { isBlankOptionVisible } from '@/helpers/spreadsheetHelper';
import { useDebounce } from '@/hooks/useDebounce';

const FILTER_VALUES_LIMIT = 10000;

export const OptionList = ({
  colId,
  searchQuery,
  multipleValue,
  setMultipleValue,
  fieldType,
  filterOperatorType,
}) => {
  const [loading, setIsLoading] = useState(false);
  const [defaultOptionList, setDefaultOptionList] = useState([]);
  const [filteredOptions, setFilteredOptions] = useState([]);
  const [isAllOptionsSelected, setIsAllOptionsSelected] = useState(false);
  const [isSelectAllAllowed, setIsSelectAllAllowed] = useState(false);
  const debouncedSearchValue = useDebounce(searchQuery, 500);

  const listRef = useRef();

  const handleScroll = ({ target }) => {
    const { scrollTop } = target;

    listRef.current.scrollTo(scrollTop);
  };

  const getAutocompleteData = async () => {
    setIsLoading(true);
    try {
      const response = await get(
        `${AUTOCOMPLETE}/${getFileUuidFromPath()}/${colId}?${QUERY_PARAM_OMIT_EMPTY_VALUES}=${isBlankOptionVisible(
          fieldType,
          filterOperatorType
        )}`
      );
      if (response.Values) {
        setDefaultOptionList(response.Values || []);
        setFilteredOptions(response.Values || []);
      }
    } catch (error) {
      setDefaultOptionList([]);
      setFilteredOptions([]);
      console.error('Error fetching data from Quick FIlters Option List:', error);
    }
    setIsLoading(false);
  };

  useEffect(() => {
    if (!debouncedSearchValue) {
      getAutocompleteData();
    }
  }, [debouncedSearchValue]);

  useEffect(() => {
    setIsSelectAllAllowed(
      (filteredOptions.length > 0 && filteredOptions.length <= FILTER_SELECT_ALL_LIMIT) ||
        defaultOptionList.length <= FILTER_SELECT_ALL_LIMIT
    );
  }, [defaultOptionList, filteredOptions]);

  useEffect(() => {
    const getQuery = async (query) => {
      setIsLoading(true);
      try {
        return await get(`${AUTOCOMPLETE}/${getFileUuidFromPath()}/${colId}/${query}`);
      } catch (e) {
        setFilteredOptions([]);
        setDefaultOptionList([]);
        console.error(e);
        return [];
      } finally {
        setIsLoading(false);
      }
    };
    if (debouncedSearchValue.length > 0) {
      getQuery(debouncedSearchValue).then((data) => {
        if (data?.Success) {
          setFilteredOptions(data.Values || []);
        }
      });
    }
  }, [debouncedSearchValue]);

  const handleCheckboxValuesChange = (event, option) => {
    const { checked } = event.target;
    setIsAllOptionsSelected(false);
    if (option === 'Select All') {
      const allValues = searchQuery ? [...filteredOptions, searchQuery] : [...filteredOptions];
      setIsAllOptionsSelected(checked ? true : false);
      setMultipleValue(checked ? allValues : []);
    } else {
      let updatedValues;
      if (option === searchQuery && !filteredOptions.includes(searchQuery)) {
        updatedValues = [...multipleValue, option];
      } else {
        updatedValues = checked
          ? [...multipleValue, option]
          : multipleValue.filter((item) => item !== option);
      }
      setMultipleValue(updatedValues);
    }
  };
  const shouldShowSearchQueryCheckbox = searchQuery && !filteredOptions.includes(searchQuery);

  return (
    <div className='bg-white'>
      <div className='mt-2 border rounded border-shadow-200 text-ui-secondary'>
        {!loading && isSelectAllAllowed && (
          <div className='border-b rounded-b-none w-full cursor-pointer text-ui hover:bg-ocean-blue-100  hover:text-ui hover:rounded hover:rounded-b-none px-2 h-8 pt-[2px]'>
            <div className='flex items-center py-[3px]'>
              <CheckboxInput
                labelClassName='text-xs'
                label={
                  multipleValue.length === defaultOptionList.length || isAllOptionsSelected
                    ? 'Deselect All'
                    : 'Select All'
                }
                checked={multipleValue.length === defaultOptionList.length || isAllOptionsSelected}
                onChange={(event) => {
                  handleCheckboxValuesChange(event, 'Select All');
                }}
                className='cursor-pointer'
                dataCy='qf-option-selectAll'
              />
            </div>
          </div>
        )}
        <Scrollbars autoHeight autoHeightMin={80} autoHeightMax={150} onScroll={handleScroll}>
          {loading ? (
            <div
              data-cy='qf-value-loader'
              className='absolute top-0 bottom-0 left-0 right-0 flex items-center justify-center'
            >
              <Spinner size='medium' color='shadow' />
            </div>
          ) : (
            <>
              {shouldShowSearchQueryCheckbox && (
                <div className='!w-auto pl-2 pr-2 text-sm cursor-pointer text-ui hover:bg-ocean-blue-100 hover:text-ui hover:rounded hover:w-auto whitespace-nowrap'>
                  <div className='flex items-center py-2'>
                    <CheckboxInput
                      label={searchQuery}
                      checked={multipleValue.includes(searchQuery)}
                      onChange={(event) => handleCheckboxValuesChange(event, searchQuery)}
                      dataCy='qf-option-query-search'
                      className='cursor-pointer'
                    />
                  </div>
                </div>
              )}
              <FixedSizeList
                itemCount={filteredOptions.length}
                itemSize={32}
                height={150}
                style={{ overflow: false, display: 'block' }}
                ref={listRef}
              >
                {(props) =>
                  Option({
                    ...props,
                    filteredOptions,
                    multipleValue,
                    handleCheckboxValuesChange,
                  })
                }
              </FixedSizeList>
            </>
          )}

          {/* </ul> */}
        </Scrollbars>
      </div>
      {!loading && filteredOptions?.length >= FILTER_VALUES_LIMIT && (
        <div
          role='button'
          tabIndex={0}
          onKeyUp={() => {}}
          className='flex flex-row items-center gap-1 mt-[12px]'
        >
          <Icon name='question' size={14} color='#D9B500' alt='Not all values showing' />
          <span className='text-[10px] font-extrabold tracking-wide uppercase font-overpass text-carrot-800'>
            Not All values showing
          </span>
        </div>
      )}
    </div>
  );
};
